import { languageToLocale } from './language.js';

// Returns ordinal suffix for a day number (1st, 2nd, 3rd, etc)
const nth = function (d) {
  if (d > 3 && d < 21) return 'th';
  switch (d % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

// Formats a date into a relative time string (e.g. "2 hours ago", "Yesterday")
const getDateDisplay = date => {
  const currentLanguage = i18next.language;
  const dateObj = new Date(date);
  const diffTime = Math.abs(new Date().valueOf() - dateObj.valueOf());
  let days = diffTime / (24 * 60 * 60 * 1000);
  let hours = (days % 1) * 24;
  let minutes = (hours % 1) * 60;
  let secs = (minutes % 1) * 60;
  [days, hours, minutes, secs] = [
    Math.floor(days),
    Math.floor(hours),
    Math.floor(minutes),
    Math.floor(secs),
  ];

  if (days > 0) {
    if (days === 1) {
      return currentLanguage === 'en' ? 'Yesterday' : '어제';
    }

    return (
      dateObj.toLocaleDateString(languageToLocale[currentLanguage], {
        year: 'numeric',
      }) +
      ' ' +
      dateObj.toLocaleDateString(languageToLocale[currentLanguage], {
        month: 'short',
      }) +
      ' ' +
      dateObj.toLocaleDateString(languageToLocale[currentLanguage], {
        day: 'numeric',
      }) +
      (currentLanguage === 'en' ? nth(dateObj.getDate()) : '')
    );
  }

  if (currentLanguage === 'kr') {
    if (hours > 0) {
      return `${hours} 시간 전`;
    }

    if (minutes > 0) {
      return `${minutes} 분 전`;
    }
    return `${secs} 초 전`;
  } else {
    if (hours > 0) {
      return `${hours} ${hours >= 2 ? 'hours' : 'hour'} ago`;
    }
    if (minutes > 0) {
      return `${minutes} ${minutes >= 2 ? 'minutes' : 'minute'} ago`;
    }
    return `${secs} ${secs >= 2 ? 'seconds' : 'second'} ago`;
  }
};

// Validates and parses a date string or Date object, returns null if invalid
const parseValidDate = value => {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    return isValidDate(date) ? date : null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

// Formats a date for HTML date input fields (YYYY-MM-DD)
const formatDateForInput = isoString => {
  if (!isoString) return '';
  const date = parseValidDate(isoString);
  if (!date) return '';

  // Use local date components to avoid timezone conversion issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Formats a date in a localized format (e.g. "21 Jun 2025")
const formatDueDate = dateString => {
  if (!dateString) return '';
  const date = parseValidDate(dateString);
  if (!date) return '';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

// Calculates the number of days between two dates
const differenceInDays = (laterDate, earlierDate) => {
  const timeDiff = laterDate.getTime() - earlierDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

// Checks if a value is a valid Date object
const isValidDate = date => {
  return date instanceof Date && !isNaN(date.getTime());
};

export {
  getDateDisplay,
  parseValidDate,
  formatDateForInput,
  formatDueDate,
  differenceInDays,
  isValidDate,
};
